'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PromoCodeInputProps {
  onApply: (code: string) => void;
}

const PromoCodeInput = ({ onApply }: PromoCodeInputProps) => {
  const [promoCode, setPromoCode] = useState('');
  const [isApplied, setIsApplied] = useState(false);
  const [error, setError] = useState('');

  const availablePromoCodes = [
    { code: 'FRESH10', discount: 10, description: '10% off on first order' },
    { code: 'SAVE50', discount: 50, description: '₹50 off on orders above ₹500' },
    { code: 'FISHBITE20', discount: 20, description: '20% off on all fish products' },
  ];

  const handleApply = () => {
    const validCode = availablePromoCodes.find(
      (promo) => promo.code.toLowerCase() === promoCode.toLowerCase()
    );

    if (validCode) {
      setIsApplied(true);
      setError('');
      onApply(promoCode);
    } else {
      setError('Invalid promo code');
      setIsApplied(false);
    }
  };

  const handleRemove = () => {
    setPromoCode('');
    setIsApplied(false);
    setError('');
    onApply('');
  };

  return (
    <div className="bg-card border border-border rounded-md p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon name="TicketIcon" size={20} className="text-primary" />
        <h3 className="font-heading font-semibold text-base text-foreground">
          Apply Promo Code
        </h3>
      </div>

      <div className="flex gap-2 mb-3">
        <div className="flex-1 relative">
          <input
            type="text"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            placeholder="Enter promo code"
            disabled={isApplied}
            className="w-full px-4 py-2 bg-background border border-input rounded-md text-sm focus-ring disabled:opacity-50 disabled:cursor-not-allowed"
          />
          {isApplied && (
            <Icon 
              name="CheckCircleIcon" 
              size={20} 
              className="absolute right-3 top-1/2 -translate-y-1/2 text-success"
            />
          )}
        </div>
        {!isApplied ? (
          <button
            onClick={handleApply}
            disabled={!promoCode}
            className="px-6 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift disabled:opacity-50 disabled:cursor-not-allowed focus-ring"
          >
            Apply
          </button>
        ) : (
          <button
            onClick={handleRemove}
            className="px-6 py-2 bg-error text-error-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift focus-ring"
          >
            Remove
          </button>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 text-error text-sm mb-3">
          <Icon name="ExclamationCircleIcon" size={16} />
          <span>{error}</span>
        </div>
      )}

      {isApplied && (
        <div className="bg-success/10 border border-success/20 rounded-md p-3 mb-3">
          <div className="flex items-center gap-2 text-success text-sm">
            <Icon name="CheckCircleIcon" size={16} />
            <span className="font-medium">Promo code applied successfully!</span>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="text-xs font-medium text-muted-foreground mb-2">
          Available Offers:
        </div>
        {availablePromoCodes.map((promo) => (
          <button
            key={promo.code}
            onClick={() => {
              setPromoCode(promo.code);
              setError('');
            }}
            className="w-full flex items-center justify-between p-3 bg-muted rounded-md transition-smooth hover:bg-border"
          >
            <div className="text-left">
              <div className="font-data font-medium text-sm text-foreground">
                {promo.code}
              </div>
              <div className="text-xs text-muted-foreground">
                {promo.description}
              </div>
            </div>
            <Icon name="ChevronRightIcon" size={16} className="text-muted-foreground" />
          </button>
        ))}
      </div>
    </div>
  );
};

export default PromoCodeInput;